<script>
    import Swiper from "@/plugins/swiper-bundle";
    import TheSliderArrow from "@main/packages/TheSlider/components/TheSliderArrow";
    import { config } from "@main/packages/TheSlider/config";

    export default {
        name: "TheSlider",
        components: { TheSliderArrow },
        props: {
            numberSlides: {
                default: 1,
            },
            options: {
                type: Object,
            },
            nameSlider: {
                required: true,
                type: String,
            },
            isScrollbar: {
                default: false,
                type: Boolean,
            },
            isBullets: {
                default: false,
                type: Boolean,
            },
            bulletsOptions: {
                type: Object,
                default: () => {},
            },
            isArrow: {
                default: true,
                type: Boolean,
            },
            arrowPosition: {
                type: String,
                default: "center",
            },
            list: [Array, Object],
            eventsEmitting: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            const options = {};
            if (this.isBullets) {
                let circleHtml = "";
                if (this.bulletsOptions.type === "circle") {
                    circleHtml = config.bullets.innerHTML;
                }
                options.pagination = {
                    el: `.${this.nameSlider}-dots`,
                    clickable: true,
                    dynamicBullets: this.bulletsOptions.dynamicBullets,
                    renderBullet: (_, className) => {
                        return `
                                <span class="${className} c-slider__${this.bulletsOptionsData.type} outline" style="animation-duration: ${this.slideChangeTimer}ms;">
                                    ${circleHtml}
                                </span>`;
                    },
                };
            }
            if (this.isScrollbar) {
                options.scrollbar = {
                    el: `.${this.nameSlider}-scrollbar`,
                    hide: false,
                    draggable: true,
                };
            }
            return {
                currentSlider: null,
                swiperOption: {
                    ...options,
                    centredSlides: true,
                    watchOverflow: true,
                    slidesPerView: this.numberSlides,
                    navigation: {
                        nextEl: `.${this.nameSlider}-next`,
                        prevEl: `.${this.nameSlider}-prev`,
                    },
                    mousewheel: {
                        forceToAxis: true,
                    },
                    ...this.options,
                },
                bulletsOptionsData: {
                    ...config.bullets.options,
                    ...this.bulletsOptions,
                },
                slideChangeTimer: 0,
            };
        },
        computed: {
            namePrevArrow() {
                return [`${this.nameSlider}-prev`, `c-slider__arrow--${this.arrowPosition}`];
            },
            nameNextArrow() {
                return [`${this.nameSlider}-next`, `c-slider__arrow--${this.arrowPosition}`];
            },
            nameDots() {
                return `${this.nameSlider}-dots ${this.$root.$_name}-dots
                ${this.bulletsOptionsData.animated ? "is-animated-dots" : ""}`;
            },
            classNameSlider() {
                return `js-${this.nameSlider}`;
            },
            numberSlotSliderItem() {
                return this.$slots["swiper-slide"] ? this.$slots["swiper-slide"].length : 0;
            },
            computeSlidesCount() {
                return this.numberSlotSliderItem || this.list?.length;
            },
            showScrollbar() {
                return this.isScrollbar && this.computeSlidesCount > this.numberSlides;
            },
            showBullets() {
                return this.isBullets && this.computeSlidesCount > this.numberSlides;
            },
            showBottomWrapper() {
                return (
                    this.showBullets ||
                    (this.isArrow && this.computeSlidesCount > this.numberSlides)
                );
            },
        },
        watch: {
            list: {
                handler() {
                    setTimeout(() => {
                        this.currentSlider.update();
                        this.currentSlider.scrollbar.updateSize();
                    }, 100);
                },
            },
        },
        mounted() {
            this.slideChangeTimer = this.swiperOption.autoplay?.delay || 0;
            this.$nextTick(() => {
                this.currentSlider = new Swiper(`.${this.classNameSlider}`, {
                    init: false,
                    ...this.swiperOption,
                });
                /**
                 * Callback после инициализации слайдера
                 */
                if (Array.isArray(this.currentSlider)) {
                    this.currentSlider = this.currentSlider[0];
                }
                this.currentSlider.on("init", (slider) => {
                    if (this.eventsEmitting) {
                        this.$emit("on-init-slider");
                    }
                    if (this.isBullets && this.bulletsOptionsData.animated) {
                        config.bullets.init(slider);
                    }
                });

                this.currentSlider.init();

                if (this.eventsEmitting) {
                    /**
                     * Callback после переключения слайдов
                     */
                    this.currentSlider.on("slideChange", (slider) => {
                        this.$emit("on-slide-changed", slider.activeIndex);
                        if (this.isBullets && this.bulletsOptionsData.animated) {
                            config.bullets.animate(slider);
                        }
                    });
                }
                setTimeout(() => {
                    if (this.currentSlider.$wrapperEl) {
                        this.$emit("on-update");
                        this.currentSlider.updateSlides();
                    }
                }, 700);
            });
        },
    };
</script>

<template>
    <div class="c-slider">
        <div class="c-slider__swiper swiper-container" :class="classNameSlider">
            <div class="swiper-wrapper">
                <slot name="swiper-slide"></slot>
            </div>
        </div>

        <the-slider-arrow
            v-if="isArrow && arrowPosition === 'center'"
            type="prev"
            :class-name="namePrevArrow"
        ></the-slider-arrow>

        <the-slider-arrow
            v-if="isArrow && arrowPosition === 'center'"
            type="next"
            :class-name="nameNextArrow"
        ></the-slider-arrow>

        <div
            v-show="showScrollbar"
            class="swiper-scrollbar c-slider__scrollbar"
            :class="`${this.nameSlider}-scrollbar`"
        ></div>

        <div
            v-show="showBottomWrapper"
            class="c-slider__bottom-wrapper"
            :class="[`${nameSlider}-bottom-wrapper`]"
        >
            <the-slider-arrow
                v-if="isArrow && arrowPosition === 'bottom'"
                type="prev"
                :class-name="namePrevArrow"
            ></the-slider-arrow>

            <div
                v-show="showBullets"
                class="swiper-dots c-slider__bullets"
                :class="nameDots"
            ></div>

            <the-slider-arrow
                v-if="isArrow && arrowPosition === 'bottom'"
                type="next"
                :class-name="nameNextArrow"
            ></the-slider-arrow>
        </div>
    </div>
</template>

<style lang="scss">
    //@import "./swiper";
    .swiper-pagination-lock {
        display: none !important;
    }

    .swiper-button-lock {
        display: none !important;
    }

    .c-slider {
        position: relative;
    }

    .c-slider__swiper {
        margin-bottom: 24px;

        @include bp($bp-desktop-sm) {
            margin-bottom: 12px;
        }
    }

    .c-slider__scrollbar {
        width: 100%;
        height: 2px;
        background-color: #c4cdd5;

        .swiper-scrollbar-drag {
            background-color: #182028;
        }
    }

    .c-slider__bottom-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: 16px;
    }

    .c-slider__bullets {
        white-space: nowrap;
        text-align: center;
    }

    .c-slider__bullets {
        .c-slider__dot.swiper-pagination-bullet-active {
            transform: scale(1);
        }
    }

    .c-slider__dot {
        width: 10px;
        height: 10px;
        margin: 0 8px;
        background-color: var(--slider-dot);
        border-radius: 50%;
        transition: transform 0.2s ease-in, background-color 0.2s ease-in, left 0.2s ease-in;
        opacity: 1;
        outline: none;

        &.swiper-pagination-bullet-active {
            border-radius: 50%;
            background-color: var(--slider-dot--active);
            transform: scale(1.2);
        }

        &.swiper-pagination-bullet-active-next,
        &.swiper-pagination-bullet-active-next-next {
            background-color: var(--slider-dot--next);
        }
    }

    .swiper-scrollbar-drag {
        &:after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            margin: auto;
            margin: -6px;
            cursor: grab;
        }
    }
</style>
